import { useCallback, useEffect, useState } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { useNavigate } from "react-router-dom";
import { Settings2, X, Maximize2, Minimize2, ChevronDown, ChevronUp } from "lucide-react";
import { hentBlokkData, KOLONNER, recordRute, fmtFrist, fmtVerdi, stageDot } from "./blokkData";
import PipelineKonfig from "./PipelineKonfig";
import SprintVisning from "./SprintVisning";
import MasterVisning from "./MasterVisning";
import RecordDrawer from "./RecordDrawer";

const KOL_LABEL = Object.fromEntries(KOLONNER);

const EierBadge = ({ r }) => (r.ansvarlig
  ? <span className="dok-eier-badge"><i style={{ background: r.eierFarge || "#8B5CF6" }}>{r.ansvarlig[0]}</i>{r.ansvarlig}</span>
  : <span className="dok-eier-badge uten"><i className="ut">!</i>Unassigned</span>);

export default function PipelineBlokk({ node, updateAttributes, deleteNode, editor }) {
  const spec = node.attrs.spec || {};
  const [rader, setRader] = useState(null);
  const [konfig, setKonfig] = useState(() => spec.modus === "kuratert" && !(spec.items || []).length && spec.visning !== "master");
  const [detalj, setDetalj] = useState(null);
  const [aapnet, setAapnet] = useState(false);
  const [kanRedigere, setKanRedigere] = useState(editor.isEditable);
  const navigate = useNavigate();
  const specStr = JSON.stringify(spec);

  useEffect(() => {
    const oppdater = () => setKanRedigere(editor.isEditable);
    editor.on("update", oppdater);
    return () => { editor.off("update", oppdater); };
  }, [editor]);

  const last = useCallback(() => {
    hentBlokkData({ ...JSON.parse(specStr), typ: "pipeline" }, editor.dokId)
      .then(setRader)
      .catch(() => setRader([]));
  }, [specStr, editor]);

  useEffect(() => {
    let avbrutt = false;
    hentBlokkData({ ...spec, typ: "pipeline" }, editor.dokId)
      .then((r) => { if (!avbrutt) setRader(r); })
      .catch(() => { if (!avbrutt) setRader([]); });
    return () => { avbrutt = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specStr]);

  const kolonner = (spec.kolonner || []).filter((k) => KOL_LABEL[k]);
  const endre = (endring) => updateAttributes({ spec: { ...spec, ...endring } });
  const erSprint = spec.visning === "sprint" || spec.visning === "master";
  const erMaster = spec.visning === "master";

  const kontrollKnapper = (
    <span className="dok-blokk-kontroller" contentEditable={false}>
      <button type="button" className="dok-blokk-knapp" onClick={() => endre({ bred: !spec.bred })}
        title={spec.bred ? "Normal bredde" : "Ekspander til full bredde"} data-testid={`dok-blokk-bred-${spec.bid}`}>
        {spec.bred ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
      </button>
      <button type="button" className="dok-blokk-knapp" onClick={() => setKonfig((v) => !v)} title="Innstillinger" data-testid={`dok-blokk-konfig-${spec.bid}`}><Settings2 size={12} /></button>
      <button type="button" className="dok-blokk-knapp slett"
        onClick={() => {
          // eslint-disable-next-line no-alert
          if (window.confirm("Fjerne hele blokken fra dokumentet? Tabellen forsvinner fra dokumentet, men dataene beholdes i systemet.")) deleteNode();
        }}
        title="Fjern blokk" data-testid={`dok-blokk-slett-${spec.bid}`}><X size={12} /></button>
    </span>
  );

  return (
    <NodeViewWrapper className={`dok-blokk ${erSprint ? "dok-blokk-sprint" : ""} ${spec.visning === "master" ? "dok-blokk-master" : ""} ${spec.bred ? "dok-blokk-bred" : ""}`} data-testid={`dok-blokk-pipeline-${spec.bid}`}>
      {!erMaster && (
      <div className="dok-blokk-hode" contentEditable={false}>
        {erSprint ? (
          <span className="dok-blokk-overskrift">{spec.tittel || "Priority relationships"}</span>
        ) : (
          <>
            <span className="dok-blokk-type">Pipeline · {spec.modus === "filter" ? "Filter" : "Kuratert"}{rader ? ` · ${rader.length}` : ""}</span>
            {spec.tittel && <span className="dok-blokk-navn">{spec.tittel}</span>}
          </>
        )}
        <span className="flex-1" />
        {kontrollKnapper}
      </div>
      )}
      {konfig && <PipelineKonfig spec={spec} onEndre={endre} onLukk={() => setKonfig(false)} />}
      {rader === null ? (
        <p className="dok-blokk-tom">Henter …</p>
      ) : rader.length === 0 ? (
        <p className="dok-blokk-tom">Ingen elementer — åpne innstillinger og velg records.</p>
      ) : spec.visning === "master" ? (
        <MasterVisning rader={rader} naviger={setDetalj}
          spec={spec} endre={endre} editable={kanRedigere} refresh={last} kontroller={kontrollKnapper} />
      ) : erSprint ? (
        <SprintVisning rader={rader} naviger={setDetalj}
          spec={spec} endre={endre} editable={kanRedigere} refresh={last} />
      ) : spec.visning === "kort" ? (
        <div className="dok-blokk-kortgrid" contentEditable={false}>
          {rader.map((r) => (
            <button key={`${r.type}-${r.id}`} type="button" className="dok-blokk-kort" onClick={() => setDetalj(r)} data-testid={`dok-blokk-rad-${r.id}`}>
              <p className="navn">{r.navn}</p>
              {r.under && <p className="under">{r.under}</p>}
              <span className="dok-blokk-chip"><i className="dok-blokk-dot" style={{ background: stageDot(r.stage) }} />{r.stageLabel}</span>
            </button>
          ))}
        </div>
      ) : spec.kollaps && !aapnet ? (
        <button type="button" className="dok-tabell-kollaps" contentEditable={false} onClick={() => setAapnet(true)} data-testid={`dok-tabell-vis-${spec.bid}`}>
          {rader.length} rader · Vis tabell <ChevronDown size={12} />
        </button>
      ) : (
        <div className="dok-blokk-scroll" contentEditable={false}>
          {spec.kollaps && (
            <button type="button" className="dok-tabell-kollaps aapen" onClick={() => setAapnet(false)} data-testid={`dok-tabell-skjul-${spec.bid}`}>
              Skjul tabell <ChevronUp size={12} />
            </button>
          )}
          <table className="dok-blokk-tabell">
            <thead><tr><th>Navn</th>{kolonner.map((k) => <th key={k}>{KOL_LABEL[k]}</th>)}</tr></thead>
            <tbody>
              {rader.map((r) => (
                <tr key={`${r.type}-${r.id}`} onClick={() => setDetalj(r)} data-testid={`dok-blokk-rad-${r.id}`}>
                  <td className="navn">{r.navn}{r.under ? <span className="under"> — {r.under}</span> : null}</td>
                  {kolonner.map((k) => (
                    <td key={k} className={k === "neste" ? "neste" : ""}>
                      {k === "status" ? <span className="dok-blokk-chip"><i className="dok-blokk-dot" style={{ background: stageDot(r.stage) }} />{r.stageLabel}</span>
                        : k === "frist" ? (r.frist ? fmtFrist(r.frist) : "—")
                          : k === "verdi" ? (r.type === "partner" ? fmtVerdi(r.verdi) : "—")
                            : k === "neste" ? (r.nesteSteg || "—")
                              : k === "ansvarlig" ? <EierBadge r={r} />
                                : (r[k] || "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detalj && (
        <RecordDrawer
          rad={(rader || []).find((x) => x.id === detalj.id && x.type === detalj.type) || detalj}
          onLukk={() => setDetalj(null)}
          refresh={last}
        />
      )}
    </NodeViewWrapper>
  );
}
